<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {computed, ref} from "vue";
import {useQuery} from "@tanstack/vue-query";
import BaseModal from "@/components/global-themed/BaseModal.vue";
import axios from "axios";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import TitleAndBody from './modals/TitleAndBody.vue'
import ScheduledPost from "@/components/kit-activity-log/partials/modals/ScheduledPost.vue";
import KeywordAdded from "@/components/kit-activity-log/partials/modals/KeywordAdded.vue";
import UpdatedContentOnPage from "@/components/kit-activity-log/partials/modals/UpdatedContentOnPage.vue";
import PageContentUpdated from "@/components/kit-activity-log/partials/modals/PageContentUpdated.vue";
import GBPCategoriesSelected from "@/components/kit-activity-log/partials/modals/GBPCategoriesSelected.vue";
import GBPReviewRespondedTo from "@/components/kit-activity-log/partials/modals/GBPReviewRespondedTo.vue";
import GBPQuestionAnswered from "@/components/kit-activity-log/partials/modals/GBPQuestionAnswered.vue";
import RevertChangelogModal from "@/components/kit-activity-log/partials/modals/RevertChangelogModal.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";

const props = defineProps({
  activityLog: {
    type: Object,
    required: true,
  }
});

const modalComponents = {
  'BLOG_SCHEDULE_CREATED': ScheduledPost,
  'CONTENT_TOPIC_ADDED': TitleAndBody,
  'NEW_KEYWORD_ASSIGNED': KeywordAdded,
  'PAGE_TITLE_UPDATED': UpdatedContentOnPage,
  'META_DESCRIPTION_UPDATED': UpdatedContentOnPage,
  'H1_HEADING_UPDATED': UpdatedContentOnPage,
  'H2_HEADING_UPDATED': UpdatedContentOnPage,
  'PAGE_CONTENT_UPDATED': PageContentUpdated,
  'IMAGE_ALT_UPDATED': UpdatedContentOnPage,
  'CANONICAL_LINK_UPDATED': UpdatedContentOnPage,
  'META_ROBOTS_CHANGED': UpdatedContentOnPage,
  'INTERNAL_LINKS_ADDED': UpdatedContentOnPage,
  'GBP_CATEGORIES_SELECTED': GBPCategoriesSelected,
  'GBP_BUSINESS_DESCRIPTION_UPDATED': UpdatedContentOnPage,
  'GBP_REVIEW_RESPONDED_TO': GBPReviewRespondedTo,
  'GBP_FAQ_CREATED': GBPQuestionAnswered,
  'GBP_QUESTION_ANSWERED': GBPQuestionAnswered,
  'GBP_POST_CREATED': ScheduledPost,
  'GBP_LOCATION_URL_UPDATED': UpdatedContentOnPage,
};

const subcategory = computed(() => props.activityLog.subcategory_name);

const modalComponent = computed(() => modalComponents[subcategory.value] || undefined);

const redirectUrl = computed(() => {
  switch (subcategory.value) {
    case 'SEO_STRATEGY_CREATED':
      return `/sitemap`;
    // case 'TOV_CREATED':
    //   return `/agent-settings#tone-of-voice`; // TODO HIKE-4323 remove for now
    case 'COMPETITOR_ADDED':
      return `/competition/${props.activityLog.activity.id}`;
    case 'BLOG_POST_WRITTEN':
    case 'NEW_PAGE_CONTENT_CREATED':
      return props.activityLog.activity.redirect_url;
  }

  return undefined;
});

const showModal = ref(false);
const showRevertModal = ref(false);

const {data, isLoading, refetch} = useQuery({
  queryKey: ['activity-log-content', props.activityLog.id],
  queryFn: async function () {
    const {data} = await axios.get(`/ajax/v2/seo-agent-activity-log/${props.activityLog.id}/content`);

    return data;
  },
  enabled: showModal.value,
})

const handleClick = () => {
  if (redirectUrl.value) {
    window.open(redirectUrl.value, '_blank');
    return;
  }

  showModal.value = true;
  refetch();
}

const handleRevertClick = () => {
  showModal.value = false;
  showRevertModal.value = true;
}

const handleRevertClose = () => {
  showRevertModal.value = false;
}
</script>

<template>
  <DefaultButton
    v-if="redirectUrl || !!modalComponent"
    variant="base"
    @click.prevent="handleClick"
  >
    View
  </DefaultButton>
  <Teleport
    v-if="showModal"
    to=".tailwind"
  >
    <BaseModal
      modal-class="tw-min-w-[800px] tw-max-w-[90%]"
      @close="showModal = false"
    >
      <ModalContentWrapper>
        <div class="tw-flex tw-flex-col tw-gap-6">
          <LoadingBlock v-if="isLoading" />
          <component
            :is="modalComponent"
            v-else-if="modalComponent"
            :data="data"
            :modal-title="activityLog.subcategory"
          />
          <div class="tw-flex tw-items-center tw-gap-4 tw-justify-end">
            <DefaultButton
              v-if="activityLog.can_undo_fix && !activityLog.activity.manual_implementation"
              variant="danger"
              @click="handleRevertClick"
            >
              Revert
            </DefaultButton>

            <DefaultButton
              variant="primary-inverted"
              @click="showModal = false"
            >
              Close
            </DefaultButton>
          </div>
        </div>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>

  <RevertChangelogModal
    v-if="showRevertModal"
    :activity-log-id="activityLog.id"
    @close="handleRevertClose"
  />
</template>
